const DEFAULT_CAP_TOKEN_TTL_MS = 110000;

function normalizeCapToken(value) {
  if (typeof value === "string") {
    return value.trim();
  }
  if (value && typeof value === "object") {
    if (typeof value.detail?.token === "string") {
      return value.detail.token.trim();
    }
    if (typeof value.token === "string") {
      return value.token.trim();
    }
  }
  return "";
}

export function createCapSolver({
  endpointInputId,
  tokenInputId,
  tokenTtlMs = DEFAULT_CAP_TOKEN_TTL_MS,
}) {
  let capClient = null;
  let capInFlight = null;
  let capSolvedAt = 0;

  function getEndpoint() {
    const input = document.getElementById(endpointInputId);
    return input ? String(input.value || "").trim() : "";
  }

  function getToken() {
    const input = document.getElementById(tokenInputId);
    return input ? String(input.value || "").trim() : "";
  }

  function setToken(value) {
    const input = document.getElementById(tokenInputId);
    const token = normalizeCapToken(value);
    if (input) {
      input.value = token;
    }
    capSolvedAt = token ? Date.now() : 0;
  }

  function isEnabled() {
    return Boolean(getEndpoint());
  }

  function hasFreshToken() {
    const token = getToken();
    if (!token) {
      return false;
    }
    return Date.now() - capSolvedAt < tokenTtlMs;
  }

  function getOrCreateClient() {
    const apiEndpoint = getEndpoint();
    if (!apiEndpoint || !window.Cap) {
      return null;
    }
    if (!capClient || capClient.__apiEndpoint !== apiEndpoint) {
      capClient = new window.Cap({ apiEndpoint });
      capClient.__apiEndpoint = apiEndpoint;
    }
    return capClient;
  }

  async function solve(force = false) {
    if (!isEnabled()) {
      return "";
    }
    if (!force && hasFreshToken()) {
      return getToken();
    }
    if (capInFlight) {
      return capInFlight;
    }

    capInFlight = (async () => {
      const client = getOrCreateClient();
      if (!client) {
        throw new Error("CAP client is not available");
      }

      const result = await client.solve();
      const token = normalizeCapToken(result?.token || client.token);
      if (!token) {
        throw new Error("CAP solve did not return a token");
      }

      setToken(token);
      return token;
    })();

    try {
      return await capInFlight;
    } finally {
      capInFlight = null;
    }
  }

  function prime() {
    if (!isEnabled() || hasFreshToken() || capInFlight) {
      return;
    }
    solve(false).catch((error) => {
      console.debug("CAP pre-solve failed:", error);
    });
  }

  async function headers(extraHeaders = {}, { force = true } = {}) {
    const headersWithCaptcha = { ...extraHeaders };
    if (isEnabled()) {
      headersWithCaptcha["X-Cap-Token"] = await solve(force);
    }
    return headersWithCaptcha;
  }

  return {
    headers,
    isEnabled,
    prime,
    solve,
  };
}
